import { tokens } from '@neo4j-ndl/base';
import classNames from 'classnames';
import { Children, forwardRef, isValidElement, useLayoutEffect, useRef, useState } from 'react';
import type * as GridLayout from 'react-grid-layout';
import { Responsive, WidthProvider } from 'react-grid-layout';
import type { ResponsiveProps } from 'react-grid-layout';

import './grid.css';

function replacePx(value: string): number {
  return Number(value.replace('px', ''));
}

type AdvancedGridItemProps = {
  children: React.ReactNode;
  className?: string;
  style?: React.CSSProperties;
};

const AdvancedGridItem = forwardRef<HTMLDivElement, AdvancedGridItemProps>(
  ({ children, className, style, ...restProps }, ref) => {
    const classes = classNames('grid-item', {}, className);
    return (
      <div ref={ref} className={classes} style={style} {...restProps}>
        {children}
      </div>
    );
  },
);
AdvancedGridItem.displayName = 'AdvancedGrid.Item';

const xSmallGutter = replacePx(tokens.space['4']);
const smallGutter = replacePx(tokens.space['4']);
const mediumGutter = replacePx(tokens.space['6']);
const largeGutter = replacePx(tokens.space['6']);

const xSmallContentBreakpoint = replacePx(tokens.breakpointNextTwo['5xs']);
const smallContentBreakpoint = replacePx(tokens.breakpointNextTwo['2xs']);
const mediumContentBreakpoint = replacePx(tokens.breakpointNextTwo.md);
const largeContentBreakpoint = replacePx(tokens.breakpointNextTwo['2xl']);

// Pixel height of each row.
const rowHeight = 10;

const advancedGridConfiguration: {
  [key in 'x-small' | 'small' | 'medium' | 'large']: {
    grid: {
      margin: [number, number];
      containerPadding: [number, number];
      cols: number;
      breakpoint: number;
    };
    item: {
      w: number;
      h: number;
    };
  };
} = {
  'x-small': {
    grid: {
      // gutter
      margin: [xSmallGutter, smallGutter],
      // removes padding
      containerPadding: [0, 0],
      cols: 1,
      breakpoint: xSmallContentBreakpoint,
    },
    item: {
      w: 8,
      h: 1,
    },
  },
  small: {
    grid: {
      // gutter
      margin: [smallGutter, smallGutter],
      // removes padding
      containerPadding: [0, 0],
      cols: 8,
      breakpoint: smallContentBreakpoint,
    },
    item: {
      w: 8,
      h: 1,
    },
  },
  medium: {
    grid: {
      // gutter
      margin: [mediumGutter, mediumGutter],
      // removes padding
      containerPadding: [0, 0],
      cols: 12,
      breakpoint: mediumContentBreakpoint,
    },
    item: {
      w: 6,
      h: 1,
    },
  },
  large: {
    grid: {
      // gutter
      margin: [largeGutter, largeGutter],
      // removes padding
      containerPadding: [0, 0],
      cols: 12,
      breakpoint: largeContentBreakpoint,
    },
    item: {
      w: 4,
      h: 1,
    },
  },
};

interface AdvancedGridItemPosition {
  row: number;
  column: number;
}

function getAdvancedGridItemPosition(w: number, cols: number, index: number): AdvancedGridItemPosition {
  const numberPerRow = cols / w;
  const row = Math.ceil((index + 1) / numberPerRow) - 1;
  const column = (index % numberPerRow) * w;
  return { row, column };
}

function generateAdvancedGridConfiguration(prop: keyof (typeof advancedGridConfiguration)['small']['grid']) {
  return Object.entries(advancedGridConfiguration).reduce((accumulator, [key, value]) => {
    return {
      ...accumulator,
      [key]: value.grid[prop],
    };
  }, {});
}

const ResponsiveGridLayout = WidthProvider<ResponsiveProps>(Responsive);
type ResponsiveGridLayoutType = InstanceType<typeof ResponsiveGridLayout>;

export type AdvancedGridProps = {
  children: React.ReactNode;
  className?: string;
  style?: React.CSSProperties;
  reactGridLayoutProps?: ResponsiveGridLayoutType['props'];
  shouldUseAutoHeight?: boolean;
};

/**
 * AdvancedGrid is a responsive grid layout component that uses react-grid-layout under the hood.
 * It is designed to be used with the AdvancedGrid.Item component.
 *
 * To utilize the auto-height feature, set the shouldUseAutoHeight prop to true.
 * This will automatically calculate the height of the grid based on the height of the items.
 *
 * To manually use this component, set the shouldUseAutoHeight prop to false.
 * reactGridLayoutProps should be used to pass any props to the react-grid-layout component.
 */
const AdvancedGridComponent = ({
  children,
  className,
  style,
  reactGridLayoutProps,
  shouldUseAutoHeight = false,
  ...restProps
}: AdvancedGridProps) => {
  const childRefs = useRef<React.RefObject<HTMLDivElement>[]>([]);
  const [size, setSize] = useState({ width: 0, height: 0 });

  // We want to get a hold of the largest height of all the items.
  useLayoutEffect(() => {
    if (!shouldUseAutoHeight) {
      return;
    }
    let maxWidth = 0;
    let maxHeight = 0;
    childRefs.current.forEach((ref, index) => {
      if (ref.current === null) {
        return;
      }
      const width = ref.current.offsetWidth;
      const height = ref.current.offsetHeight;
      maxWidth = Math.max(maxWidth, width);
      maxHeight = Math.max(maxHeight, height);
    });
    setSize({ width: maxWidth, height: maxHeight });
  }, [children, shouldUseAutoHeight]);

  let props;
  let newChildren: React.ReactNode;
  if (shouldUseAutoHeight) {
    const h = Math.ceil(size.height / rowHeight);
    // We are generating a layout for each breakpoint from the configuration object.
    const layouts: Record<string, { i: string; x: number; w: number; y: number; h: number }[]> = {};
    Children.toArray(children)
      .filter((child, index) => {
        // const isNeedleLayoutAdvancedGridItem =
        //   isValidElement(child) && typeof child.type !== 'string' && child.type.displayName === 'AdvancedGrid.Item';
        const isNeedleLayoutAdvancedGridItem = isValidElement(child) && child.type === AdvancedGridItem;
        return isNeedleLayoutAdvancedGridItem;
      })
      .forEach((child, index) => {
        Object.entries(advancedGridConfiguration).forEach(([key, value]) => {
          if (layouts[key] === undefined) {
            layouts[key] = [];
          }
          // react-grid-layout uses the key prop to map items to their grid, this is unfortunate as
          // React uses the same prop for internal use. React alters this key by adding .$ when transforming children
          // to an array via Children.toArray.
          const itemKey = isValidElement(child) && child.key !== null ? String(child.key).replace('.$', '') : '';
          const { row, column } = getAdvancedGridItemPosition(value.item.w, value.grid.cols, index);
          layouts[key].push({
            i: itemKey,
            x: column,
            w: value.item.w,
            y: h * row,
            h: h,
          });
        });
      });

    newChildren = (
      <>
        {Children.map(children, (child, index) => {
          const isNeedleLayoutAdvancedGridItem = isValidElement(child) && child.type === AdvancedGridItem;
          if (!isNeedleLayoutAdvancedGridItem) {
            return child;
          }
          const itemKey = isValidElement(child) && child.key !== null ? String(child.key).replace('.$', '') : '';
          const fn = (el: HTMLDivElement | null) => {
            childRefs.current[index] = { current: el };
          };
          return (
            <div key={itemKey}>
              <div ref={fn}>{child}</div>
            </div>
          );
        })}
      </>
    );

    const classes = classNames(`advanced-grid`, {}, className);
    const margin: [number, number] = [0, 0];
    const containerPadding: [number, number] = [0, 0];
    props = {
      className: classes,
      style,
      ...restProps,
      margin,
      containerPadding,
      rowHeight,
      layouts,
      cols: generateAdvancedGridConfiguration('cols'),
      breakpoints: generateAdvancedGridConfiguration('breakpoint'),
    };
  } else {
    newChildren = children;
    props = reactGridLayoutProps;
  }

  return <ResponsiveGridLayout {...props}>{newChildren}</ResponsiveGridLayout>;
};
AdvancedGridComponent.displayName = 'AdvancedGrid';

export type GridProps = {
  children: React.ReactNode;
  className?: string;
  style?: React.CSSProperties;
  autoHeight?: boolean;
  reactGridLayoutProps?: GridLayout['props'];
};

// Simple grid using css classes.
const GridComponent = forwardRef<HTMLDivElement, GridProps>(
  ({ children, className, style, ...restProps }, ref: React.ForwardedRef<HTMLDivElement>) => {
    const classes = classNames('simple-grid', className);

    return (
      <div ref={ref} className={classes} style={style} {...restProps}>
        {children}
      </div>
    );
  },
);
GridComponent.displayName = 'Grid';

type NewBreakpoints = 'min' | keyof typeof tokens.breakpointNextTwo;

type GridItemProps = {
  children?: React.ReactNode;
  className?: string;
  style?: React.CSSProperties;
  // TODO: change from number to allow for auto and limit to 12?
  cols?: Partial<Record<NewBreakpoints, number>>;
};
const GridItem = forwardRef<HTMLDivElement, GridItemProps>(
  ({ children, className, style, cols, ...restProps }, ref: React.ForwardedRef<HTMLDivElement>) => {
    const classes = classNames('simple-grid-item', className);

    const getGridColumnStyle = () => {
      return Object.entries(cols ?? {}).reduce((acc, [token, span]) => {
        return {
          ...acc,
          [`--grid-column-span-${token}`]: span,
        };
      }, {});
    };

    return (
      <div
        ref={ref}
        className={classes}
        style={{
          ...style,
          ...getGridColumnStyle(),
        }}
        {...restProps}
      >
        {children}
      </div>
    );
  },
);
GridItem.displayName = 'Grid.Item';

// Issue with TypeScript forwardRef and subcomponents: https://github.com/DefinitelyTyped/DefinitelyTyped/issues/34757#issuecomment-894053907
// Fixes issue with: Accordion.Item = Item;
const AdvancedGrid = Object.assign(AdvancedGridComponent, {
  Item: AdvancedGridItem,
});

const Grid = Object.assign(GridComponent, {
  Item: GridItem,
});

export { Grid, AdvancedGrid, advancedGridConfiguration };
